import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getGroupDisruptions, exportGroupDisruptions } from "../../lib/api";
import { triggerBlobDownload } from "../../lib/download";
import { printPage } from "../../lib/print";

const currentSchoolYear = () => {
  const now = new Date();
  const y = now.getMonth() + 1 >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return { start: `${y}-08-01`, end: `${y + 1}-07-31` };
};

export default function GroupDisruptionsTab() {
  const sy = currentSchoolYear();
  const [start, setStart] = useState(sy.start);
  const [end, setEnd] = useState(sy.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState("all");  // for the trend chart
  const [drillGroup, setDrillGroup] = useState(null);       // group_id for incident drill-down
  const [byStudent, setByStudent] = useState(false);
  const [drillStudent, setDrillStudent] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getGroupDisruptions({ start, end });
      setData(res.data);
    } catch {
      toast.error("Failed to load group disruptions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    return (data.weekly || []).map((w) => ({
      week: w.week_start?.slice(5),
      value: groupFilter === "all" ? w.total : (w.by_group?.[groupFilter] || 0),
    }));
  }, [data, groupFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportGroupDisruptions({ start, end });
      triggerBlobDownload(new Blob([res.data]), `Group_Disruptions_${start}_to_${end}.xlsx`);
      toast.success("Exported to Excel");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    if (!data) return;
    const rows = (data.by_group || []).map((g) =>
      `<tr><td>${g.group_name}</td><td style="text-align:center">${g.count}</td><td style="text-align:center">${g.pct}%</td></tr>`).join("");
    const studentRows = (data.by_student || []).map((s) =>
      `<tr><td>${s.name}</td><td style="text-align:center">${s.count}</td></tr>`).join("");
    const content = `
      <h2 style="color:#00305E;margin-bottom:4px;">Group Disruptions</h2>
      <p style="color:#666;margin-top:0;">${start} to ${end} · ${data.total} total class moves</p>
      <h3 style="color:#00305E;">By Group</h3>
      <table style="width:100%;border-collapse:collapse;" border="1" cellpadding="6">
        <thead><tr style="background:#00305E;color:#fff;"><th align="left">Group</th><th>Disruptions</th><th>% of Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <h3 style="color:#00305E;margin-top:20px;">By Student</h3>
      <table style="width:100%;border-collapse:collapse;" border="1" cellpadding="6">
        <thead><tr style="background:#00305E;color:#fff;"><th align="left">Student</th><th>Class Moves Caused</th></tr></thead>
        <tbody>${studentRows}</tbody>
      </table>`;
    printPage({ title: "Group Disruptions", subtitle: "SMARTIS", content, extraStyles: "body{max-width:800px;margin:0 auto;padding:40px;}" });
  };

  const drillIncidents = useMemo(() => {
    if (!data) return [];
    let list = data.incidents || [];
    if (drillGroup) list = list.filter((i) => i.group_id === drillGroup);
    if (drillStudent) list = list.filter((i) => i.student_id === drillStudent);
    return list;
  }, [data, drillGroup, drillStudent]);

  return (
    <div className="space-y-6" data-testid="group-disruptions-tab">
      <Card>
        <CardHeader>
          <CardTitle>Group Disruptions</CardTitle>
          <CardDescription>Class moves caused by student disruption — which groups had to relocate, how often, and by whom.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-[160px]" data-testid="gd-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[160px]" data-testid="gd-end-date" />
            </div>
            <Button onClick={load} className="bg-[#00305E] hover:bg-[#00305E]/90 h-9" data-testid="gd-apply-btn">Apply</Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={handlePrint} disabled={!data} className="h-9" data-testid="gd-print-btn">Print / PDF</Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting || !data} className="h-9" data-testid="gd-export-btn">{exporting ? "Exporting…" : "Export Excel"}</Button>
          </div>

          {loading ? (
            <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : !data || data.total === 0 ? (
            <p className="text-center text-muted-foreground py-10" data-testid="gd-empty">No class moves recorded for this date range.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground" data-testid="gd-total">{data.total} total class move{data.total === 1 ? "" : "s"} in range.</p>

              {/* Toggle: By Group / By Student */}
              <div className="flex gap-2">
                <Button size="sm" variant={byStudent ? "outline" : "default"} className={byStudent ? "" : "bg-[#00305E] hover:bg-[#00305E]/90"} onClick={() => { setByStudent(false); setDrillStudent(null); }} data-testid="gd-view-group">By Group</Button>
                <Button size="sm" variant={byStudent ? "default" : "outline"} className={byStudent ? "bg-[#00305E] hover:bg-[#00305E]/90" : ""} onClick={() => { setByStudent(true); setDrillGroup(null); }} data-testid="gd-view-student">By Student</Button>
              </div>

              {!byStudent ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#00305E] text-white">
                      <tr><th className="text-left px-3 py-2">Group</th><th className="px-3 py-2 w-32">Disruptions</th><th className="px-3 py-2 w-28">% of Total</th></tr>
                    </thead>
                    <tbody>
                      {data.by_group.map((g) => (
                        <tr key={g.group_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setDrillGroup(drillGroup === g.group_id ? null : g.group_id)} data-testid={`gd-group-row-${g.group_id}`}>
                          <td className="px-3 py-2 font-medium text-[#00305E]">{g.group_name}</td>
                          <td className="px-3 py-2 text-center">{g.count}</td>
                          <td className="px-3 py-2 text-center">{g.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#00305E] text-white">
                      <tr><th className="text-left px-3 py-2">Student</th><th className="px-3 py-2 w-40">Class Moves Caused</th></tr>
                    </thead>
                    <tbody>
                      {data.by_student.map((s) => (
                        <tr key={s.student_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setDrillStudent(drillStudent === s.student_id ? null : s.student_id)} data-testid={`gd-student-row-${s.student_id}`}>
                          <td className="px-3 py-2 font-medium text-[#00305E]">{s.name}</td>
                          <td className="px-3 py-2 text-center">{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Drill-down incident list */}
              {(drillGroup || drillStudent) && (
                <div className="border rounded-lg p-3 bg-slate-50" data-testid="gd-drilldown">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-semibold text-[#00305E]">Incidents ({drillIncidents.length})</h4>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDrillGroup(null); setDrillStudent(null); }}>Clear</Button>
                  </div>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {drillIncidents.map((i) => (
                      <div key={i.id} className="text-xs bg-white border rounded px-2 py-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span className="font-medium">{i.date}</span>
                        <span>{i.student_name}</span>
                        <span className="text-muted-foreground">Group: {i.group_name}</span>
                        {i.period && <span className="text-muted-foreground">Period: {i.period}</span>}
                        {i.moved_to && <span className="text-muted-foreground">→ {i.moved_to}</span>}
                        <span className="text-muted-foreground">Logged by {i.reporter_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly trend */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-[#00305E]">Weekly trend</h4>
                  <Select value={groupFilter} onValueChange={setGroupFilter}>
                    <SelectTrigger className="w-[200px] h-9" data-testid="gd-trend-group-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All groups</SelectItem>
                      {data.groups.map((g) => (<SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" name="Disruptions" stroke="#00305E" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* By period / time of day */}
              <div className="pt-2">
                <h4 className="text-sm font-semibold text-[#00305E] mb-2">By period / time of day</h4>
                <div className="flex flex-wrap gap-2">
                  {data.by_period.map((p) => (
                    <div key={p.period} className="px-3 py-2 rounded-lg border bg-white text-sm" data-testid={`gd-period-${p.period}`}>
                      <span className="font-medium">{p.period}</span>: {p.count}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
